import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Input, Row, Col } from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";
import { MODULOS, permisosDesdeLegacy } from "../../../permisos/permisos";

// Editor de permisos por pantalla: Ver + acciones (crear, editar, cancelar...).
// Sale todo de catalogo.json, asi que una pantalla nueva aparece aqui sola.

// Permisos actuales de un usuario (si aun no se migra, se calculan de sus booleanos viejos).
const permisosDe = (u) => (Array.isArray(u.permisos) ? u.permisos : permisosDesdeLegacy(u));

function EditorPermisos({ isOpen, toggle, usuario, usuarios, alGuardar }) {
  const URL_USERS = process.env.REACT_APP_URL_USERS;
  const [sel, setSel] = useState(new Set());
  const [copiarDe, setCopiarDe] = useState("");
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (usuario) setSel(new Set(permisosDe(usuario)));
    setCopiarDe("");
  }, [usuario]);

  if (!usuario) return null;

  const tiene = (k) => sel.has(k);
  const cambia = (fn) =>
    setSel((prev) => {
      const s = new Set(prev);
      fn(s);
      return s;
    });

  const clavesPantalla = (p) => [`${p.key}.ver`, ...p.acciones.map((a) => `${p.key}.${a.key}`)];
  const clavesModulo = (m) => m.pantallas.flatMap(clavesPantalla);
  const todas = MODULOS.flatMap(clavesModulo);

  // Quitar "ver" quita sus acciones; dar una accion da "ver".
  const toggleVer = (p) =>
    cambia((s) => {
      if (s.has(`${p.key}.ver`)) clavesPantalla(p).forEach((k) => s.delete(k));
      else s.add(`${p.key}.ver`);
    });
  const toggleAccion = (p, a) =>
    cambia((s) => {
      const k = `${p.key}.${a.key}`;
      if (s.has(k)) s.delete(k);
      else {
        s.add(k);
        s.add(`${p.key}.ver`);
      }
    });
  const moduloCompleto = (m) => clavesModulo(m).every(tiene);
  const toggleModulo = (m) =>
    cambia((s) => {
      const completo = clavesModulo(m).every((k) => s.has(k));
      clavesModulo(m).forEach((k) => (completo ? s.delete(k) : s.add(k)));
    });
  const pantallaCompleta = (p) => clavesPantalla(p).every(tiene);
  const togglePantalla = (p) =>
    cambia((s) => {
      const completa = clavesPantalla(p).every((k) => s.has(k));
      clavesPantalla(p).forEach((k) => (completa ? s.delete(k) : s.add(k)));
    });

  function copiar(id) {
    setCopiarDe(id);
    const otro = usuarios.find((u) => u._id === id);
    if (otro) setSel(new Set(permisosDe(otro)));
  }

  function guardar() {
    setGuardando(true);
    // Solo se guardan claves que existen en el catalogo (por si quedo algo viejo).
    const validas = new Set(todas);
    const permisos = [...sel].filter((k) => validas.has(k)).sort();
    axios
      .patch(
        `${URL_USERS}/${usuario._id}`,
        { permisos },
        { headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` } }
      )
      .then(() => {
        setGuardando(false);
        Swal.fire(
          "Permisos guardados",
          "Se aplican la próxima vez que el usuario inicie sesión.",
          "success"
        );
        alGuardar && alGuardar(usuario._id, permisos);
        toggle();
      })
      .catch((error) => {
        setGuardando(false);
        Swal.fire({
          icon: "error",
          title: "No se pudieron guardar",
          footer: `${error.response ? JSON.stringify(error.response.data) : error}`,
        });
      });
  }

  const totalSel = todas.filter(tiene).length;

  return (
    <Modal size="xl" isOpen={isOpen} toggle={toggle}>
      <ModalHeader toggle={toggle}>
        Permisos de {usuario.nombre} {usuario.apellido}
      </ModalHeader>
      <ModalBody>
        <Row className="align-items-end" style={{ marginBottom: 12 }}>
          <Col md={5}>
            <small className="text-muted">Copiar los permisos de otro usuario</small>
            <Input type="select" bsSize="sm" value={copiarDe} onChange={(e) => copiar(e.target.value)}>
              <option value="">Selecciona...</option>
              {usuarios
                .filter((u) => u._id !== usuario._id && u.is_active !== "No")
                .sort((a, b) => (`${a.nombre} ${a.apellido}` > `${b.nombre} ${b.apellido}` ? 1 : -1))
                .map((u) => (
                  <option key={u._id} value={u._id}>
                    {u.nombre} {u.apellido}
                  </option>
                ))}
            </Input>
          </Col>
          <Col md={7} style={{ textAlign: "right" }}>
            <small className="text-muted" style={{ marginRight: 10 }}>
              {totalSel} de {todas.length} permisos
            </small>
            <Button size="sm" color="success" onClick={() => setSel(new Set(todas))}>
              Marcar todo
            </Button>{" "}
            <Button size="sm" color="secondary" outline onClick={() => setSel(new Set())}>
              Quitar todo
            </Button>
          </Col>
        </Row>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(420px, 1fr))",
            gap: 12,
          }}
        >
          {MODULOS.map((m) => (
            <div key={m.key} style={{ border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  margin: 0,
                  padding: "8px 12px",
                  background: "#f8fafc",
                  borderBottom: "1px solid #e2e8f0",
                  cursor: "pointer",
                  fontWeight: 700,
                }}
              >
                <input type="checkbox" checked={moduloCompleto(m)} onChange={() => toggleModulo(m)} />
                <i className={m.icono} style={{ color: "#6366f1" }} />
                {m.nombre}
              </label>
              <div style={{ padding: "4px 12px 8px" }}>
                {m.pantallas.map((p) => (
                  <div
                    key={p.key}
                    style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "4px 14px", padding: "5px 0", borderBottom: "1px dashed #f1f5f9" }}
                  >
                    <span
                      onClick={() => togglePantalla(p)}
                      title="Clic para marcar/quitar todo en esta pantalla"
                      style={{ minWidth: 170, fontWeight: 600, fontSize: "0.85rem", cursor: "pointer", color: pantallaCompleta(p) ? "#0f172a" : "#475569" }}
                    >
                      {p.nombre}
                    </span>
                    <label style={{ display: "flex", alignItems: "center", gap: 4, margin: 0, fontSize: "0.8rem" }}>
                      <input type="checkbox" checked={tiene(`${p.key}.ver`)} onChange={() => toggleVer(p)} />
                      Ver
                    </label>
                    {p.acciones.map((a) => (
                      <label
                        key={a.key}
                        style={{ display: "flex", alignItems: "center", gap: 4, margin: 0, fontSize: "0.8rem", color: "#475569" }}
                      >
                        <input type="checkbox" checked={tiene(`${p.key}.${a.key}`)} onChange={() => toggleAccion(p, a)} />
                        {a.nombre}
                      </label>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </ModalBody>
      <ModalFooter>
        <small className="text-muted" style={{ marginRight: "auto" }}>
          Los cambios aplican cuando el usuario vuelve a iniciar sesión.
        </small>
        <Button color="secondary" onClick={toggle}>
          Cancelar
        </Button>
        <Button color="success" onClick={guardar} disabled={guardando}>
          <i className="fas fa-save" style={{ marginRight: 6 }} /> Guardar
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default EditorPermisos;
